/**
 * The Mon–Sun month grid (POS2-1259 CP3).
 *
 * Clicking a cell selects the day; double-clicking opens Add event prefilled with that date.
 * `onAddForDay` is undefined for a viewer without `occupancy:update`, so the affordance simply
 * is not there rather than being there and refused.
 */
import type { DayCovers } from "@/hooks/use-occupancy";
import { useTranslation } from "@/i18n/use-translation";
import { monthGrid, monthKey, orderedEvents, TYPE_COLOURS } from "@/lib/occupancy";
import { cn } from "@/lib/utils";

const WEEKDAY_KEYS = [
  "occupancy.weekday.mon",
  "occupancy.weekday.tue",
  "occupancy.weekday.wed",
  "occupancy.weekday.thu",
  "occupancy.weekday.fri",
  "occupancy.weekday.sat",
  "occupancy.weekday.sun",
] as const;

/** At most two chips per cell, then "+n more" — the cell has to stay one row tall. */
const MAX_CHIPS = 2;

export function MonthView({
  month,
  days,
  capacity,
  today,
  selectedDate,
  onSelectDay,
  onAddForDay,
}: {
  month: string;
  days: DayCovers[];
  capacity: number | null;
  today: string;
  selectedDate: string | null;
  onSelectDay: (date: string) => void;
  /** Double-click opens Add event prefilled with that date. Undefined for a read-only viewer. */
  onAddForDay?: (date: string) => void;
}) {
  const { t } = useTranslation();
  const byDate = new Map(days.map((day) => [day.date, day]));

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      <div className="grid grid-cols-7 border-b border-border">
        {WEEKDAY_KEYS.map((key) => (
          <div
            key={key}
            className="px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
          >
            {t(key)}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {monthGrid(month).map((date) => {
          const day = byDate.get(date);
          const outOfMonth = monthKey(date) !== month;
          const events = day ? orderedEvents(day.events) : [];
          const overCapacity = capacity !== null && (day?.total ?? 0) > capacity;

          return (
            <button
              key={date}
              type="button"
              onClick={() => onSelectDay(date)}
              onDoubleClick={() => onAddForDay?.(date)}
              aria-pressed={selectedDate === date}
              className={cn(
                "flex h-[104px] flex-col gap-1 border-b border-r border-border p-2 text-left align-top transition-colors last:border-r-0 hover:bg-muted/50",
                outOfMonth && "bg-muted/30 text-muted-foreground",
                selectedDate === date && "bg-muted",
              )}
            >
              <div className="flex items-baseline gap-1.5">
                <span
                  className={cn(
                    "text-xs font-semibold tabular-nums",
                    date === today && "rounded-full bg-foreground px-1.5 py-0.5 text-background",
                  )}
                >
                  {Number(date.slice(8))}
                </span>
                {day?.total !== null && day !== undefined && (
                  <span
                    className={cn(
                      "ml-auto text-xs font-bold tabular-nums",
                      overCapacity ? "text-destructive" : "text-foreground",
                    )}
                  >
                    {day.total}
                  </span>
                )}
              </div>

              <div className="flex min-w-0 flex-col gap-0.5">
                {events.slice(0, MAX_CHIPS).map((event) => (
                  <span
                    key={event.id}
                    // `truncate` plus `min-w-0` on the parent is what keeps a long event name
                    // from widening the grid — no horizontal scroll at any viewport.
                    className="flex min-w-0 items-center gap-1 rounded px-1 py-0.5 text-[11px]"
                    style={{ background: `${TYPE_COLOURS[event.event_type]}14` }}
                  >
                    <span
                      className="size-1.5 shrink-0 rounded-[1px]"
                      style={{ background: TYPE_COLOURS[event.event_type] }}
                    />
                    <span className="truncate">{event.name}</span>
                  </span>
                ))}
                {events.length > MAX_CHIPS && (
                  <span className="px-1 text-[11px] text-muted-foreground">
                    {t("occupancy.month.moreEvents", { count: events.length - MAX_CHIPS })}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
